// Shared read/write helpers for the `tag_sm` field (mandatory-category validation + the
// selected-tags-by-category record saved alongside content). Current format is a
// JSON-stringified {category, name, value} per entry, e.g.
//   '{"category":"Player","name":"Rory McIlroy","value":"rory-mcilroy-34024"}'
// Older saved content still has plain "Category/Tag" strings - parseTagSmEntry supports both.
export type TagSmEntry = {
  category: string;
  name: string;
  value: string;
};

export const buildTagSmEntry = (category: string, name: string, value: string = name): string =>
  JSON.stringify({ category, name, value });

// TODO: once every tag_sm writer has migrated to buildTagSmEntry and no legacy-format content
// remains in the database, delete the legacy "Category/Tag" branch below - this can then just
// JSON.parse directly.
export const parseTagSmEntry = (entry: unknown): TagSmEntry | null => {
  if (typeof entry !== "string" || !entry) return null;
  try {
    const parsed = JSON.parse(entry);
    if (parsed && typeof parsed === "object" && typeof parsed.category === "string") {
      return {
        category: parsed.category,
        name: typeof parsed.name === "string" ? parsed.name : "",
        value: typeof parsed.value === "string" ? parsed.value : "",
      };
    }
  } catch {
    // Not JSON - fall through to the legacy plain-string format below.
  }
  const separatorIndex = entry.indexOf("/");
  if (separatorIndex <= 0) return null;
  const category = entry.slice(0, separatorIndex);
  const tag = entry.slice(separatorIndex + 1);
  if (!tag) return null;
  return { category, name: tag, value: tag };
};

export const parseTagSmEntries = (tagSm: unknown): TagSmEntry[] =>
  Array.isArray(tagSm)
    ? tagSm
        .map((entry) => parseTagSmEntry(entry))
        .filter((entry): entry is TagSmEntry => entry !== null)
    : [];

export const tagSmHasCategory = (tagSm: unknown, category: string): boolean =>
  parseTagSmEntries(tagSm).some((entry) => entry.category === category);

// Finds the index of the entry for (category, name) regardless of which format it was stored
// in - use this instead of tagSm.indexOf(buildTagSmEntry(...)) when removing an entry, since a
// tag_sm array loaded from already-saved (possibly legacy-format) content won't string-match a
// freshly-built new-format entry even when it's logically the same tag.
export const findTagSmEntryIndex = (tagSm: unknown, category: string, name: string): number => {
  if (!Array.isArray(tagSm)) return -1;
  return tagSm.findIndex((entry) => {
    const parsed = parseTagSmEntry(entry);
    return parsed?.category === category && parsed?.name === name;
  });
};

// Drops repeated (category, name) pairs, keeping the first occurrence. A tag_sm array can pick
// up an exact duplicate when it's rebuilt by matching tag names against a master tag taxonomy
// that itself lists the same tag under a category more than once - collapse those back down to
// one entry per (category, name) before writing the array back to state.
export const dedupeTagSmEntries = (tagSm: unknown): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  parseTagSmEntries(tagSm).forEach((entry) => {
    const key = `${entry.category}\u0000${entry.name}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(buildTagSmEntry(entry.category, entry.name, entry.value));
  });
  return result;
};
